import {
    Badge,
    type BadgeColor,
    type BadgeSize,
    type BadgeType,
} from '@trustoo/components/Badge/v2'
import React from 'react'
import { CodeSnippet, DemoAvatar, InlineCode, MIcon, Section, titleCase } from './shared'

// ---------- Badge (v2) showcase ----------
// Generated from Figma's "Badge" (5:2938) ComponentSet. The color prop is
// scoped to the ramps that have utility-* tokens (gray/brand/error/warning/
// success[/orange]); blue-gray/indigo/purple/pink from Figma are out of scope
// until those ramps exist.

const BADGE_COLORS: BadgeColor[] = ['gray', 'brand', 'error', 'warning', 'success', 'orange']

const BADGE_TYPES: { type: BadgeType; label: string; help: React.ReactNode }[] = [
    {
        type: 'pill-color',
        label: 'Pill color',
        help: (
            <>
                Fully rounded (<InlineCode>rounded-radius-full</InlineCode>) with a tinted{' '}
                <InlineCode>utility-*</InlineCode> fill, border and text.
            </>
        ),
    },
    {
        type: 'badge-color',
        label: 'Badge color',
        help: (
            <>
                Same tinted fill, but squared corners (<InlineCode>rounded-radius-sm</InlineCode>,{' '}
                <InlineCode>rounded-radius-md</InlineCode> at lg).
            </>
        ),
    },
    {
        type: 'badge-modern',
        label: 'Badge modern',
        help: (
            <>
                Neutral white surface + primary border + <InlineCode>shadow-xs</InlineCode>; the{' '}
                <InlineCode>color</InlineCode> only tints the leading dot.
            </>
        ),
    },
]

const BADGE_USAGE = `import { Badge } from '@trustoo/components/Badge/v2'

<Badge color="success">Active</Badge>
<Badge type="badge-color" color="warning" dot>Pending</Badge>
<Badge type="badge-modern" color="brand" dot>New</Badge>

// Adornments (composable slots, not an icon enum)
<Badge color="brand" iconLeading={<Icon />}>Label</Badge>
<Badge color="gray" avatar={<Avatar />}>Olivia</Badge>
<Badge color="gray" flag={<Flag />}>Netherlands</Badge>
<Badge color="brand" onClose={() => remove(id)}>Dismissible</Badge>
<Badge color="brand" iconOnly iconLeading={<Icon />} aria-label="Verified" />`

export const BadgeShowcase: React.FC = () => (
    <Section
        title="Badge (v2 component)"
        description={
            <>
                Generated from Figma's <span className="font-semibold">Badge</span> set
                (ComponentSet <InlineCode>5:2938</InlineCode>). A small inline status/label chip.
                Import v2 explicitly via <InlineCode>@trustoo/components/Badge/v2</InlineCode>{' '}
                (net-new component — the existing <InlineCode>superBadge</InlineCode> trust badge is
                a separate, unrelated component).
            </>
        }>
        <div className="space-y-spacing-3xl">
            {BADGE_TYPES.map(({ type, label, help }) => (
                <div key={type} className="space-y-spacing-md">
                    <div>
                        <h3 className="text-sm font-semibold text-text-primary">{label}</h3>
                        <p className="mt-spacing-xs text-xs text-text-tertiary">{help}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-spacing-md rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                        {BADGE_COLORS.map((color) => (
                            <Badge
                                key={color}
                                type={type}
                                color={color}
                                dot={type === 'badge-modern'}>
                                {titleCase(color)}
                            </Badge>
                        ))}
                    </div>
                </div>
            ))}

            <div className="space-y-spacing-md">
                <h3 className="text-sm font-semibold text-text-primary">Sizes</h3>
                <div className="flex flex-wrap items-center gap-spacing-lg rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    {(['sm', 'md', 'lg'] as BadgeSize[]).map((size) => (
                        <Badge key={size} size={size} color="brand" dot>
                            {size.toUpperCase()}
                        </Badge>
                    ))}
                </div>
            </div>

            <div className="space-y-spacing-md">
                <div>
                    <h3 className="text-sm font-semibold text-text-primary">Adornments</h3>
                    <p className="mt-spacing-xs text-xs text-text-tertiary">
                        The Figma <InlineCode>Icon</InlineCode> axis is decomposed into composable
                        slot props rather than one enum: <InlineCode>dot</InlineCode>,{' '}
                        <InlineCode>iconLeading</InlineCode>, <InlineCode>iconTrailing</InlineCode>,{' '}
                        <InlineCode>avatar</InlineCode>, <InlineCode>flag</InlineCode>,{' '}
                        <InlineCode>onClose</InlineCode> and <InlineCode>iconOnly</InlineCode>.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-spacing-md rounded-radius-xs border border-border-secondary bg-base-white p-spacing-xl">
                    <Badge color="success" dot>
                        Dot
                    </Badge>
                    <Badge
                        color="brand"
                        iconLeading={<MIcon name="bolt" className="text-[14px]" />}>
                        Leading icon
                    </Badge>
                    <Badge
                        color="brand"
                        iconTrailing={<MIcon name="arrow_outward" className="text-[14px]" />}>
                        Trailing icon
                    </Badge>
                    <Badge color="gray" avatar={<DemoAvatar />}>
                        Olivia
                    </Badge>
                    <Badge color="gray" flag={<span className="text-[12px] leading-[1]">🇳🇱</span>}>
                        Netherlands
                    </Badge>
                    <Badge color="brand" onClose={() => undefined}>
                        Dismissible
                    </Badge>
                    <Badge
                        color="success"
                        iconOnly
                        iconLeading={<MIcon name="check" className="text-[14px]" />}
                        aria-label="Verified"
                    />
                </div>
            </div>

            <div>
                <h3 className="mb-spacing-lg text-sm font-semibold text-text-primary">
                    How to use
                </h3>
                <CodeSnippet code={BADGE_USAGE} />
                <p className="mt-spacing-lg text-xs text-text-tertiary">
                    Props: <InlineCode>type</InlineCode> (pill-color · badge-color · badge-modern),{' '}
                    <InlineCode>color</InlineCode> (gray · brand · error · warning · success ·
                    orange), <InlineCode>size</InlineCode> (sm · md · lg), and the adornment slots
                    above. The root is a non-interactive <InlineCode>&lt;span&gt;</InlineCode>;{' '}
                    <InlineCode>onClose</InlineCode> adds a real dismiss{' '}
                    <InlineCode>&lt;button&gt;</InlineCode>, and <InlineCode>iconOnly</InlineCode>{' '}
                    requires an <InlineCode>aria-label</InlineCode>.
                </p>
            </div>
        </div>
    </Section>
)
